import { PatientWithStatsInhalerUsage } from '@/jsonapi/PatientWithStats'
import moment from 'moment-timezone'
import { fillDate } from '@/components/patient-stats/common/utils'
import { buildRescueInhalerSeries } from '@/components/patient-stats/graphs/summary-reports/chart-options/rescue-inhaler-charts/buildRescueInhalerSeries'
import { USER_TIME_ZONE } from '@/utils/time'
import { PatientWithEventsEvent } from '@/jsonapi/PatientWithEvents'

export function rescueInhalerChartOptions(options: {
  timezone: string
  rescueInhalerUsagesDaily: PatientWithStatsInhalerUsage[]
  startTimestamp: number
  endTimestamp: number
  colors: any
  relieverInhalerEvents: PatientWithEventsEvent[]
  threshold?: number
}) {
  const {
    rescueInhalerUsagesDaily,
    startTimestamp,
    endTimestamp,
    colors,
    relieverInhalerEvents,
    threshold = 4,
  } = options

  const timezone = options.timezone || USER_TIME_ZONE
  const xAxisLabels = [...fillDate(timezone, startTimestamp, endTimestamp)].map((timestamp, i) => String(timestamp))

  const { maxValue, seriesOption } = buildRescueInhalerSeries(rescueInhalerUsagesDaily, timezone, threshold)

  const filledDateWithHasProblem = xAxisLabels.reduce((acc, cur) => {
    acc[cur] = false
    return acc
  }, {})

  relieverInhalerEvents.forEach(event => {
    filledDateWithHasProblem[
      moment(event.eventTimestamp)
        .startOf('day')
        .valueOf()
    ] = true
  })

  const disableAxisOptions = {
    axisLine: { show: false },
    axisTick: { show: false },
    splitLine: { show: false },
    axisLabel: { show: false },
  }

  const xAxisOptions = {
    data: xAxisLabels,
    type: 'category',
    axisLine: { show: false },
    axisTick: {
      show: false,
      alignWithLabel: true,
    },
    splitLine: { show: false },
    axisLabel: {
      show: true,
      showMinLabel: true,
      showMaxLabel: true,
      interval: xAxisLabels.length - 1,
      formatter: function(params) {
        return moment(parseInt(params, 10))
          .tz(timezone)
          .format('MMM D')
      },
    },
  }

  return {
    grid: [
      { left: '10%', top: 60, right: '10%', bottom: 60 },
      { left: '10%', top: 60, right: '10%', bottom: 53 },
    ],
    xAxis: [
      {
        ...xAxisOptions,
        axisLine: {
          show: true,
          lineStyle: {
            color: 'rgba(6, 64, 80, 0.3)', // 0f45cd
            width: 2,
          },
        },
      },
      {
        ...xAxisOptions,
        ...disableAxisOptions,
        gridIndex: 1,
      },
    ],
    yAxis: [
      {
        type: 'value',
        splitNumber: 1,
        axisLine: { show: false },
        splitLine: { show: false },
        axisLabel: { show: false },
        min: 0,
        max: Math.max(8, maxValue),
      },
      {
        ...disableAxisOptions,
        gridIndex: 1,
        type: 'value',
        min: 0,
        max: 48,
      },
    ],
    series: [
      seriesOption,
      {
        type: 'bar',
        silent: true,
        data: Object.entries(filledDateWithHasProblem).map(([key, value]) => {
          return {
            value: [key, 1],
            itemStyle: {
              color: value ? colors.burgundy : colors.teal,
            },
          }
        }),
        xAxisIndex: 1,
        yAxisIndex: 1,
      },
    ],
    tooltip: {
      trigger: 'item',
    },
  }
}
